import type { FormEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { validateInviteEmail } from '@/lib/auth/constants';
import { Button } from '@/lib/base/buttons/button';
import {
  FormField,
  FormProvider,
  Input,
  SubmitButton,
  useFormContext,
} from '@/lib/base/forms';
import type { FieldConfig } from '@/lib/base/forms/hooks/useFormErrors';
import { Select } from '@/lib/base/forms/select/select';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { getPortfolioActionError } from '@/lib/portfolio/errors';
import { useCreateInvitation } from '@/lib/portfolio/hooks/useCreateInvitation';
import type { PortfolioRole } from '@/lib/portfolio/types';

const ROLE_ITEMS = [
  { id: 'Admin', label: 'Admin' },
  { id: 'User', label: 'User' },
];

interface InviteMemberModalProps {
  portfolioId: number;
  orgId: number;
  /** Display name of the target org — shown in the modal title so a cross-org
   * admin always sees exactly which org the invite lands in. */
  orgName: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// =============================================================================
// InviteMemberForm — inner component that accesses FormContext
//
// Separated from the outer component so it can call useFormContext() as a
// descendant of FormProvider.
// =============================================================================

interface InviteMemberFormProps {
  portfolioId: number;
  orgId: number;
  orgName: string;
  isOpen: boolean;
  email: string;
  roleName: PortfolioRole;
  onEmailChange: (value: string) => void;
  onRoleChange: (key: string | number | null) => void;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

function InviteMemberForm({
  portfolioId,
  orgId,
  orgName,
  isOpen,
  email,
  roleName,
  onEmailChange,
  onRoleChange,
  onOpenChange,
  onSuccess,
}: InviteMemberFormProps) {
  const {
    setSubmitError,
    touchAll,
    isValid,
    errorSummary,
    scrollToField,
    submitError,
    resetTouched,
  } = useFormContext();
  const [banner, setBanner] = useState<string | null>(null);
  const createMutation = useCreateInvitation(portfolioId, orgId);

  const handleSubmit = useCallback(async () => {
    setBanner(null);
    try {
      await createMutation.mutateAsync({ email: email.trim(), roleName });
      resetTouched();
      onSuccess();
    } catch (err) {
      const message = getPortfolioActionError(err);
      setSubmitError(message);
      setBanner(message);
    }
  }, [
    createMutation,
    email,
    roleName,
    onSuccess,
    setSubmitError,
    resetTouched,
  ]);

  // Route native form submission (Enter in the email field) through the same
  // validation gate the SubmitButton uses, so keyboard submit works without
  // bypassing field validation.
  const handleFormSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (submitError) {
        setSubmitError(null);
      }
      touchAll();
      const onlySubmitError = !!submitError && errorSummary.length === 0;
      if (isValid || onlySubmitError) {
        void handleSubmit();
      } else if (errorSummary.length > 0) {
        requestAnimationFrame(() => scrollToField(errorSummary[0].name));
      }
    },
    [
      submitError,
      setSubmitError,
      touchAll,
      isValid,
      errorSummary,
      scrollToField,
      handleSubmit,
    ]
  );

  // Clear the stale error banner (and its paired submitError) the moment the user
  // edits the email or role — otherwise an error for the previous submission stays
  // visible while they correct the form, so the banner no longer matches the input.
  const handleEmailChange = useCallback(
    (value: string) => {
      setBanner(null);
      if (submitError) {
        setSubmitError(null);
      }
      onEmailChange(value);
    },
    [onEmailChange, submitError, setSubmitError]
  );

  const handleRoleSelectionChange = useCallback(
    (key: string | number | null) => {
      setBanner(null);
      if (submitError) {
        setSubmitError(null);
      }
      onRoleChange(key);
    },
    [onRoleChange, submitError, setSubmitError]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setBanner(null);
        resetTouched();
      }
      onOpenChange(open);
    },
    [onOpenChange, resetTouched]
  );

  const handleCancel = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  // Actions live in the Dialog's sticky footer slot (standard px-6 pt-4 pb-6
  // spacing) rather than inline in the form body, so the buttons get the same
  // breathing room as every other form modal. SubmitButton is FormProvider-
  // aware via onClick, so it works from the footer without a form= wire-up.
  const footer = useMemo(
    () => (
      <div className='flex justify-end gap-3'>
        <Button
          color='secondary'
          size='md'
          onClick={handleCancel}
          isDisabled={createMutation.isPending}
        >
          Cancel
        </Button>
        <SubmitButton
          color='primary'
          size='md'
          onClick={handleSubmit}
          isDisabled={createMutation.isPending}
        >
          {createMutation.isPending ? 'Inviting…' : 'Send invite'}
        </SubmitButton>
      </div>
    ),
    [createMutation.isPending, handleCancel, handleSubmit]
  );

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Modal className='max-w-md'>
        <Dialog
          title={`Invite to ${orgName}`}
          instance='invite-member'
          footer={footer}
        >
          <form onSubmit={handleFormSubmit} noValidate className='space-y-4'>
            {banner && (
              <div
                role='alert'
                className='rounded-lg bg-[var(--color-bg-error-primary)] p-3 text-body-small text-[var(--color-text-error-primary)]'
              >
                {banner}
              </div>
            )}

            <FormField name='email' label='Email' isRequired>
              {({ isInvalid, showError, inputId }) => (
                <Input
                  id={inputId}
                  type='email'
                  value={email}
                  onChange={handleEmailChange}
                  isInvalid={isInvalid}
                  showError={showError}
                  isDisabled={createMutation.isPending}
                  placeholder='name@example.com'
                />
              )}
            </FormField>

            <Select
              label='Role'
              aria-label='Role'
              items={ROLE_ITEMS}
              selectedKey={roleName}
              onSelectionChange={handleRoleSelectionChange}
              isDisabled={createMutation.isPending}
            >
              {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
            </Select>
          </form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

// =============================================================================
// InviteMemberModal — outer component that owns state and wraps with FormProvider
// =============================================================================

export function InviteMemberModal({
  portfolioId,
  orgId,
  orgName,
  isOpen,
  onOpenChange,
}: InviteMemberModalProps) {
  const [email, setEmail] = useState('');
  const [roleName, setRoleName] = useState<PortfolioRole>('User');

  const fieldConfig = useMemo<{ email: FieldConfig }>(
    () => ({ email: { required: true, validate: validateInviteEmail } }),
    []
  );
  const fieldLabels = useMemo(() => ({ email: 'Email' }), []);
  const formValues = useMemo(() => ({ email }), [email]);

  const resetForm = useCallback(() => {
    setEmail('');
    setRoleName('User');
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        resetForm();
      }
      onOpenChange(open);
    },
    [onOpenChange, resetForm]
  );

  const handleSuccess = useCallback(() => {
    resetForm();
    onOpenChange(false);
  }, [onOpenChange, resetForm]);

  const handleRoleChange = useCallback((key: string | number | null) => {
    if (key == null) {
      return;
    }
    setRoleName(String(key) as PortfolioRole);
  }, []);

  return (
    <FormProvider
      fields={fieldConfig}
      values={formValues}
      fieldLabels={fieldLabels}
    >
      <InviteMemberForm
        portfolioId={portfolioId}
        orgId={orgId}
        orgName={orgName}
        isOpen={isOpen}
        email={email}
        roleName={roleName}
        onEmailChange={setEmail}
        onRoleChange={handleRoleChange}
        onOpenChange={handleOpenChange}
        onSuccess={handleSuccess}
      />
    </FormProvider>
  );
}
